import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Tag, IndianRupee, TrendingUp, RefreshCw, MessageCircle,
  Shield, Eye, EyeOff, Loader2, CheckCircle, BarChart3,
  Calendar, User, Copy, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, ArcElement
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate  = iso => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ADMIN_WHATSAPP = process.env.REACT_APP_ADMIN_WHATSAPP || '919876543210';

const CHART_OPTS = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 11 } } },
  },
};

export default function InfluencerDashboard() {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();

  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('overview');  // overview | analytics | coupons | settings

  // Analytics slicers
  const [chartMode, setChartMode]     = useState('monthly');   // monthly | yearly
  const [chartType, setChartType]     = useState('earnings');  // earnings | uses
  const [filterVType, setFilterVType] = useState('all');

  // Password change
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [pwForm, setPwForm]           = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw]           = useState({ c: false, n: false });
  const [pwLoading, setPwLoading]     = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/influencer/dashboard`, authHeaders);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!user || user.role !== 'influencer') { navigate('/'); return; }
    fetchDashboard();
  }, []);

  const handlePasswordChange = async () => {
    if (!pwForm.current || !pwForm.newPw) { toast.error('Fill all fields'); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Min 6 characters'); return; }
    setPwLoading(true);
    try {
      await axios.put(`${API}/influencer/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      }, authHeaders);
      toast.success('Password updated!');
      setShowPwDialog(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally { setPwLoading(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  const openAdminWhatsApp = () => {
    const msg = `Hello X7 Autoz Team,\n\nI am ${data?.profile?.name} (ID: ${data?.profile?.influencer_uid}) and I need assistance.\n\nThank you.`;
    window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // ── CHART DATA ──
  const getChartData = () => {
    if (!data) return null;
    const breakdown = chartMode === 'monthly'
      ? (data.analytics?.monthly_breakdown || [])
      : (data.analytics?.yearly_breakdown || []);

    let filtered = breakdown;
    // If vehicle type filter is applied and we have usage data per type...
    // (requires backend enhancement — showing all for now)

    const labels = filtered.map(b => b.month || b.year || '');
    const values = filtered.map(b => chartType === 'earnings'
      ? parseFloat((b.earnings || 0).toFixed(2))
      : b.uses || 0
    );

    return {
      labels,
      datasets: [{
        label: chartType === 'earnings' ? 'Earnings (₹)' : 'Coupon Uses',
        data: values,
        borderColor: '#7c3aed',
        backgroundColor: 'rgba(124,58,237,0.08)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#7c3aed',
        pointRadius: 4,
      }],
    };
  };

  const getVTypeChartData = () => {
    if (!data?.analytics?.vehicle_type_breakdown) return null;
    const map = data.analytics.vehicle_type_breakdown;
    const labels = Object.keys(map).map(k => ({ car: 'Car', bike: 'Bike', ev: 'EV', commercial: 'Commercial' }[k] || k));
    return {
      labels,
      datasets: [{
        data: Object.values(map),
        backgroundColor: ['#7c3aed', '#2563eb', '#16a34a', '#d97706'],
        borderWidth: 0,
      }],
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-purple-500 mx-auto mb-3" />
          <p className="text-gray-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { profile, coupons, analytics, payout_history } = data;
  const chartData = getChartData();
  const vtypeData = getVTypeChartData();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'coupons', label: 'My Coupons', icon: Tag },
    { id: 'settings', label: 'Settings', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal header for influencer — no city selector, no search */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-gray-800">X7 Autoz</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Growth Partner</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openAdminWhatsApp} className="text-green-600 border-green-200">
              <MessageCircle className="h-4 w-4 mr-1" /> Contact Admin
            </Button>
            <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="text-red-500">
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome banner */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-purple-200 text-sm mb-1">Welcome back,</p>
              <h1 className="text-2xl font-bold">{profile.first_name} {profile.last_name}</h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="bg-white/20 text-white text-xs font-mono px-2 py-0.5 rounded">{profile.influencer_uid}</span>
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded">{profile.city}</span>
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded">{profile.payout_cycle_days}-day payout cycle</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-purple-200 text-xs">Joined</p>
              <p className="text-white font-semibold">{fmtDate(profile.joined_date || profile.created_at)}</p>
            </div>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            <strong>Disclaimer:</strong> Earnings shown are calculated as 10% of the net discount amount (excluding GST). 
            GST collected on discount amounts is retained by X7 Autoz as per applicable tax laws. 
            All payouts are subject to verification and settlement cycles as agreed.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 flex-wrap">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                  activeTab === tab.id
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Transactions', value: analytics.total_transactions, icon: Tag, color: 'text-purple-700', bg: 'bg-purple-50' },
                { label: 'Total Earned', value: fmtMoney(analytics.total_earnings), icon: IndianRupee, color: 'text-green-700', bg: 'bg-green-50' },
                { label: 'This Month', value: fmtMoney(analytics.this_month_earnings), icon: Calendar, color: 'text-blue-700', bg: 'bg-blue-50' },
                { label: 'Pending Payout', value: fmtMoney(analytics.pending_payout), icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className={`rounded-2xl border p-4 ${s.bg}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${s.color}`} />
                      <p className="text-xs text-gray-500">{s.label}</p>
                    </div>
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                );
              })}
            </div>

            {/* 15 & 30 day earnings */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-5 text-center">
                  <p className="text-sm text-gray-500 mb-1">Last 15 Days Earnings</p>
                  <p className="text-3xl font-bold text-indigo-700">{fmtMoney(analytics.last_15_days_earnings)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5 text-center">
                  <p className="text-sm text-gray-500 mb-1">Last 30 Days Earnings</p>
                  <p className="text-3xl font-bold text-teal-700">{fmtMoney(analytics.last_30_days_earnings)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Vehicle type usage */}
            {vtypeData && (
              <Card>
                <CardHeader><CardTitle className="text-base">Vehicle Types Using Your Coupon</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="w-36 h-36 flex-shrink-0">
                      <Doughnut data={vtypeData} options={{ plugins: { legend: { display: false } }, cutout: '65%' }} />
                    </div>
                    <div className="flex-1 space-y-2">
                      {Object.entries(analytics.vehicle_type_breakdown || {}).map(([vtype, count]) => {
                        const total = Object.values(analytics.vehicle_type_breakdown).reduce((a, b) => a + b, 0);
                        const pct = total ? Math.round((count / total) * 100) : 0;
                        const colors = { car: 'bg-purple-500', bike: 'bg-blue-500', ev: 'bg-green-500', commercial: 'bg-amber-500' };
                        return (
                          <div key={vtype} className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colors[vtype] || 'bg-gray-400'}`} />
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-0.5">
                                <span className="capitalize text-gray-700">{vtype === 'ev' ? 'Electric' : vtype}</span>
                                <span className="font-medium text-gray-800">{count} ({pct}%)</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full">
                                <div className={`h-full rounded-full ${colors[vtype] || 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!Object.keys(analytics.vehicle_type_breakdown || {}).length && (
                        <p className="text-gray-400 text-sm">No usage data yet.</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payout summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Payout Summary</CardTitle>
                  <Button size="sm" variant="outline" onClick={openAdminWhatsApp} className="text-green-600 border-green-200 text-xs">
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> Contact Admin for Payout
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Total Earned</p>
                    <p className="font-bold text-gray-800">{fmtMoney(analytics.total_earnings)}</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Total Settled</p>
                    <p className="font-bold text-green-700">{fmtMoney(analytics.total_settled)}</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Pending</p>
                    <p className="font-bold text-amber-700">{fmtMoney(analytics.pending_payout)}</p>
                  </div>
                </div>
                {payout_history?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {payout_history.map(p => (
                      <div key={p.id} className="flex justify-between items-center p-2.5 bg-gray-50 rounded-xl text-sm">
                        <div>
                          <p className="font-medium text-gray-800">{fmtMoney(p.amount)}</p>
                          <p className="text-xs text-gray-400">{fmtDate(p.created_at)}{p.note ? ` · ${p.note}` : ''}</p>
                        </div>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Settled</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 text-sm py-4">No settlements recorded yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Login details */}
            <Card>
              <CardHeader><CardTitle className="text-base">Your Login Details</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-xs text-gray-500">Email (Username)</p>
                      <p className="font-medium text-gray-800">{profile.email}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(profile.email)}>
                      <Copy className="h-4 w-4 text-gray-400" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-xs text-gray-500">Login URL</p>
                      <p className="font-medium text-blue-600">www.x7autoz.com/login</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard('www.x7autoz.com/login')}>
                      <Copy className="h-4 w-4 text-gray-400" />
                    </Button>
                  </div>
                  <Button variant="outline" className="w-full text-sm" onClick={() => setShowPwDialog(true)}>
                    <Shield className="h-4 w-4 mr-2" /> Change Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Slicers */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="flex gap-2">
                    {['monthly', 'yearly'].map(m => (
                      <button key={m} onClick={() => setChartMode(m)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${chartMode === m ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {['earnings', 'uses'].map(t => (
                      <button key={t} onClick={() => setChartType(t)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${chartType === t ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {t === 'earnings' ? '₹ Earnings' : 'Coupon Uses'}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base capitalize">
                  {chartType === 'earnings' ? 'Earnings' : 'Coupon Usage'} — {chartMode} breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData && chartData.labels.length > 0 ? (
                  <div className="h-64">
                    <Line data={chartData} options={CHART_OPTS} />
                  </div>
                ) : (
                  <div className="text-center py-16 text-gray-400">
                    <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No data available yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Yearly bar if monthly selected */}
            {chartMode === 'monthly' && analytics.yearly_breakdown?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Yearly Overview</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-48">
                    <Bar
                      data={{
                        labels: analytics.yearly_breakdown.map(b => b.year),
                        datasets: [{
                          label: chartType === 'earnings' ? 'Earnings (₹)' : 'Uses',
                          data: analytics.yearly_breakdown.map(b => chartType === 'earnings' ? parseFloat((b.earnings || 0).toFixed(2)) : b.uses),
                          backgroundColor: '#7c3aed',
                          borderRadius: 6,
                        }],
                      }}
                      options={{ ...CHART_OPTS, plugins: { legend: { display: false } } }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vehicle type doughnut */}
            {vtypeData && (
              <Card>
                <CardHeader><CardTitle className="text-base">Coupon Usage by Vehicle Type</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-8">
                    <div className="w-44 h-44 flex-shrink-0">
                      <Doughnut
                        data={vtypeData}
                        options={{
                          plugins: {
                            legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 8 } },
                          },
                          cutout: '60%',
                        }}
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      {Object.entries(analytics.vehicle_type_breakdown || {}).map(([vtype, count]) => (
                        <div key={vtype} className="flex justify-between text-sm">
                          <span className="capitalize text-gray-600">{vtype === 'ev' ? 'Electric' : vtype}</span>
                          <span className="font-medium">{count} uses</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}

        {/* ── COUPONS TAB ── */}
        {activeTab === 'coupons' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {coupons.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No coupon codes assigned yet.</p>
              </div>
            ) : coupons.map(c => (
              <Card key={c.id}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-mono font-bold text-purple-700 bg-purple-50 px-4 py-2 rounded-xl tracking-widest">{c.code}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(c.code)}>
                          <Copy className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{c.discount_value}% discount</span>
                        {(c.vehicle_types || []).map(vt => (
                          <span key={vt} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">{vt}</span>
                        ))}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.active ? '● Active' : '○ Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-800">{c.used_count}</p>
                      <p className="text-xs text-gray-500">total uses</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
                    <div>
                      <p className="text-xs text-gray-500">Valid for city</p>
                      <p className="font-medium text-sm">{c.city}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">How it works</p>
                      <p className="text-xs text-gray-600">
                        Seller saves {c.discount_value}% on listing fee. You earn 10% of net discount per use.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Profile Information</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl mb-4">
                  <div className="w-14 h-14 rounded-full bg-purple-200 flex items-center justify-center text-purple-800 font-bold text-xl">
                    {profile.first_name?.charAt(0)}{profile.last_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">{profile.name}</p>
                    <p className="font-mono text-sm text-purple-700">{profile.influencer_uid}</p>
                    <p className="text-sm text-gray-500">{profile.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    { label: 'Phone', value: profile.phone },
                    { label: 'City', value: profile.city },
                    { label: 'Payout Cycle', value: `Every ${profile.payout_cycle_days} days` },
                    { label: 'Joined', value: fmtDate(profile.joined_date || profile.created_at) },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                      <p className="font-medium text-gray-800">{value || '—'}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Security</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setShowPwDialog(true)}>
                  <Shield className="h-4 w-4 mr-2" /> Change Password
                </Button>
                <Button variant="outline" className="w-full text-green-600 border-green-200" onClick={openAdminWhatsApp}>
                  <MessageCircle className="h-4 w-4 mr-2" /> Contact Admin on WhatsApp
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* ── PASSWORD CHANGE DIALOG ── */}
      <Dialog open={showPwDialog} onOpenChange={setShowPwDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showPw.c ? 'text' : 'password'}
                  value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  placeholder="Current password"
                  className="pr-10"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPw(s => ({ ...s, c: !s.c }))}>
                  {showPw.c ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPw.n ? 'text' : 'password'}
                  value={pwForm.newPw}
                  onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))}
                  placeholder="Min 6 characters"
                  className="pr-10"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPw(s => ({ ...s, n: !s.n }))}>
                  {showPw.n ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                placeholder="Repeat new password"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPwDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handlePasswordChange} disabled={pwLoading}>
                {pwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}